import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams, useSearchParams } from 'react-router-dom';
import { useErrorBoundary } from "react-error-boundary";
import { useMediaQuery } from 'react-responsive';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import PoolHeader from '../components/pools/PoolHeader';
import { MainContainer } from '../components/common/Containers';
import Banner from '../components/common/Banners';
import UserPoolCard from '../components/users/UserPoolCard';
import { PoolMeeting, PoolGroupMe, SectionLocked, AboutPool } from '../components/pools/PoolSection';
import { PoolTimeGraphic } from '../components/pools/PoolGraphic';
import CenteredModal from '../components/common/Modals';
import PoolFAQ from '../components/pools/PoolFAQ';
import Loading from '../components/common/Loading';
import { myAxiosInstance } from '../api/axiosConfig';
import { ReactComponent as NothingYetIcon } from "../assets/icons/nothingYet.svg"; // Import SVG as ReactComponent
import NotFound from "../assets/images/NotFound.png";


export default function PoolPage() {

    // The slug
    const { slug } = useParams();

    // Auth
    const { reload, setReload, userFirstName, userSlug } = useAuth();

    // Get the theme
    const theme = useTheme();

    // Errors
    const { showBoundary } = useErrorBoundary();

    // Responsive
    const isMobile = useMediaQuery({ maxWidth: 768 });

    // Get URL search params
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get initial tab from URL or default to "about"
    const tabOptions = ["about", "members", "faqs", "vote"];

    const [activeTab, setActiveTab] = useState(() => {
        const tabFromUrl = searchParams.get('tab');
        return tabOptions.includes(tabFromUrl) ? tabFromUrl : "about";
    });
    

    const { data: poolData, isLoading, error } = useQuery({
        queryKey: ['poolData', slug],
        queryFn: () =>
            myAxiosInstance.get(`/api/pools/${slug}/`).then(res => res.data),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const userStatus = useMemo(() => {
        return poolData?.user_info?.status ?? null;
      }, [poolData]);

    const [showIntroModal, setShowIntroModal] = useState(false);


    useEffect(() => {
        if (!userSlug || !slug) return;
    
        const key = `seen_intro_modal_${userSlug}_${slug}`;
        const hasSeenModal = localStorage.getItem(key);
    
        if (
            userStatus === "accepted_not_signed" &&
            !hasSeenModal
        ) {
            setShowIntroModal(true);
            localStorage.setItem(key, "true"); // only show once per user-pool combo
        }
    }, [userStatus, slug, userSlug]);    
                

    // Update URL when tab changes
    const handleTabChange = (tab) => {
        if (tabOptions.includes(tab)) {
            setActiveTab(tab);
            setSearchParams({ tab });
        }
    };

    if (isLoading) {
        return (
            <div style={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                <Loading />
            </div>
        );
    }

    if (error) {
        showBoundary(error);
        return null;
      }

    return (
        <PageWrapper theme={theme}>
            <PoolHeaderWrapper>
                <PoolHeader pool={poolData} />
        
                {/* Tabs */}
                <Tabs>
                    {tabOptions.map((tab) => (
                        <Tab
                            key={tab}
                            isActive={activeTab === tab}
                            onClick={() => handleTabChange(tab)}
                        >
                            {tab.toUpperCase()}
                        </Tab>
                    ))}
                </Tabs>
            </PoolHeaderWrapper>
    
            {/* Tab Content (just placeholder for now) */}
            <MainContainer>
                {userStatus === "not_associated" ? (
                    <NotAssociatedMessage />
                ) : (
                    <>
                        {activeTab === "about" && 
                            <>
                                {userStatus === "member" && (
                                    <>
                                        <div style={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: isMobile ? "1rem" : "1.5rem", gap: isMobile ? "1rem" : "1.5rem" }}>
                                            <PoolMeeting />
                                            <PoolGroupMe />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <AboutPool pool={poolData} />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <PoolTimeGraphic pool={poolData} />
                                        </div>
                                    </>
                                )}

                                {userStatus === "accepted_not_signed" && (
                                    <>
                                        <Banner bannerType="matched" fullBanner={true} userName={userFirstName} pool={poolData} />

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <AboutPool pool={poolData} />
                                        </div>

                                        <div style={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: isMobile ? "1rem" : "1.5rem", gap: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool next meeting is locked" />
                                            <SectionLocked text="Pool group chat is locked" />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool insights is locked" />
                                        </div>
                                    </>
                                )}

                                {userStatus === "accepted_signed" && (
                                    <>
                                        <Banner bannerType="matched" fullBanner={true} userName={userFirstName} pool={poolData} />

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <AboutPool pool={poolData} />
                                        </div>

                                        <div style={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: isMobile ? "1rem" : "1.5rem", gap: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool next meeting is locked" />
                                            <SectionLocked text="Pool group chat is locked" />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool insights is locked" />
                                        </div>
                                    </>
                                )}
                            </>
                        }

                        {activeTab === "members" && 
                            <PortfolioGrid>
                                {poolData.other_users.map((user, index) => (
                                    <UserPoolCard key={index} user={user} />
                                ))}
                            </PortfolioGrid>
                        }

                        {activeTab === "faqs" && 
                            <PoolFAQ />
                        }

                        {activeTab === "vote" && 
                            <ContentWrapper>
                                <Content>
                                    <ClockIcon />
                                    <Text style={{ fontWeight: "500" }}>
                                        No requests yet
                                    </Text>
                                    <Subtext>People who request to join {poolData.public_details.name} will appear here</Subtext>
                                </Content>
                            </ContentWrapper>
                        }
                    </>
                )}
            </MainContainer>

            <CenteredModal 
                show={showIntroModal} 
                onClose={() => { setShowIntroModal(false) }}
                media={"https://images.prospinity.com/videos/pool-intro-video.mp4"} 
                title={"WELCOME TO YOUR SUCCESS POOL"} 
                subtitle={
                    <>
                        <p>
                            Congratulations — you've been matched into a Success Pool. Each pool is thoughtfully assembled based on mutual fit and potential, so take a moment to review the group.
                        </p>
                        <p style={{ marginTop: "1rem", marginBottom: "0rem" }}>
                            When you're ready, click <span style={{ fontWeight: "500", color: theme.textPrimary }}>"REVIEW AND JOIN"</span> to see a summary of the pool and the Success Pooling Agreement.
                        </p>
                    </>
                }
                buttonsType={"one"} 
                primaryText={"SEE MY POOL"} 
                alignCenter={true} 
            />
        </PageWrapper>
    );
}


const NotAssociatedMessage = () => {

    return (
        <Container>
            <Image src={NotFound} alt="Not Part of Pool" />
            <Title>You're not part of this pool</Title>
            <Description>
                Request to join this pool to unlock details about its members, timeline, meetings, and other features.
            </Description>
            {/* <PrimaryButton size="lg">
                RETURN HOME
            </PrimaryButton> */}
        </Container>
    );
};


const PageWrapper = styled.div`
    background: ${({ theme }) => theme.backgroundDarker};
    min-height: 100vh;
`;

const PoolHeaderWrapper = styled.div`
    background: ${({ theme }) => theme.white};
    padding: 0 2.5rem;

    @media (max-width: 600px) {
        padding: 0 1rem;
    }
`;

const Tabs = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-top: 1rem;
    gap: 0.5rem;

    @media (max-width: 600px) {

    }
`;

const Tab = styled.div`
    font-size: 1.125rem;
    padding: 1rem 0.75rem;
    color: ${({ isActive, theme }) =>
        isActive ? theme.textPrimary : theme.textSecondary};
    font-weight: ${({ isActive }) => (isActive ? 500 : 400)};
    cursor: pointer;
    position: relative;

    &:after {
        content: "";
        display: ${({ isActive }) => (isActive ? "block" : "none")};
        height: 2px;
        background: ${({ theme }) => theme.primary};
        width: 100%;
        position: absolute;
        bottom: -1px;
        left: 0;
    }

    @media (max-width: 600px) {
        padding: 1rem 0.5rem;
        font-size: 1rem;
    }
`;


// Grid layout for portfolio cards
const PortfolioGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(260px, 1fr)); /* 3 cards per row initially */
    gap: 1rem;
    width: 100%;
    justify-content: center;

    @media (max-width: 1280px) {
        grid-template-columns: repeat(2, minmax(260px, 1fr)); /* 3 cards per row */
    }

    @media (max-width: 960px) {
        grid-template-columns: repeat(1, minmax(260px, 1fr)); /* 2 per row */
        place-items: center; 
    }

    @media (max-width: 600px) {
        grid-template-columns: repeat(1, 1fr); /* Full width */
        place-items: center; 
    }
`;

const ClockIcon = styled(NothingYetIcon)`
    circle {
        fill: ${({ theme }) => theme.white};
        r: 23;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
    margin-top: 4.5rem;

    @media (max-width: 600px) {
        margin-top: 0rem;
        margin-bottom: 0.5rem;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

const Text = styled.span`
    font-size: 1.25rem;
    color: ${({ theme }) => theme.textPrimary};
`;

const Subtext = styled.span`
    font-size: 1rem;
    color: ${({ theme }) => theme.textSecondary};
    margin-top: 0.25rem;
`;

// Styled components not being in a pool 
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;
    padding: 2rem;

    @media (max-width: 600px) {
        padding: 1rem;
    }
`;

const Image = styled.img`
    width: 6rem;
    height: auto;
    margin-bottom: 1rem;

    @media (max-width: 600px) {
        width: 4.5rem;
    }
`;

const Title = styled.h2`
    font-size: 1.375rem;
    font-weight: 500;
    color: ${({ theme }) => theme.textPrimary};
    margin-bottom: 0.75rem;

    @media (max-width: 600px) {
        font-size: 1.125rem;
    }
`;

const Description = styled.p`
    font-size: 1rem;
    color: ${({ theme }) => theme.textSecondary};
    max-width: 420px;
    line-height: 1.5;
    margin-bottom: 1rem;

    @media (max-width: 600px) {
        font-size: 0.875rem;
        margin-bottom: 0rem;
    }
`;

